import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import './FileUpload.css';

interface FileUploadProps {
  onDataParsed: (data: { data: any[]; headers: string[]; fileName: string }) => Promise<void> | void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onDataParsed }) => {
  const [phase, setPhase] = useState<'idle' | 'parsing' | 'saving' | 'done' | 'error'>('idle');
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const buildRows = useCallback((headers: any[], rows: any[][]) => rows.map((row) => {
    const obj: any = {};
    headers.forEach((header, index) => {
      obj[header] = row[index] || '';
    });
    return obj;
  }), []);

  const parseCSV = useCallback((file: File): Promise<{ data: any[]; headers: string[]; fileName: string }> => new Promise((resolve, reject) => {
    Papa.parse(file, {
      complete: (results) => {
        try {
          const data = results.data as string[][];
          const headers = data[0] || [];
          const rows = data.slice(1).filter((row) => row.some((cell) => String(cell).trim() !== ''));
          resolve({
            data: buildRows(headers, rows),
            headers,
            fileName: file.name
          });
        } catch (error) {
          reject(error);
        }
      },
      header: false,
      skipEmptyLines: true,
      error: (error) => reject(error)
    });
  }), [buildRows]);

  const parseExcel = useCallback((file: File): Promise<{ data: any[]; headers: string[]; fileName: string }> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][];
        const headers = jsonData[0] || [];
        const rows = jsonData.slice(1).filter((row) => row.some((cell) => cell !== undefined && cell !== ''));
        resolve({
          data: buildRows(headers, rows),
          headers,
          fileName: file.name
        });
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Unable to read the selected file.'));
    reader.readAsArrayBuffer(file);
  }), [buildRows]);

  const parseFile = useCallback(async (file: File) => {
    setPhase('parsing');
    setProgress(15);
    setErrorMessage(null);
    setStatusMessage('Parsing file...');

    const fileName = file.name;
    const fileExtension = fileName.split('.').pop()?.toLowerCase();

    try {
      let parsed;
      if (fileExtension === 'csv') {
        parsed = await parseCSV(file);
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        parsed = await parseExcel(file);
      } else {
        throw new Error('Please upload a CSV or Excel file (.csv, .xlsx, .xls).');
      }

      setProgress(55);
      setPhase('saving');
      setStatusMessage(`Preparing ${parsed.data.length.toLocaleString()} rows...`);

      await onDataParsed(parsed);
      setProgress(100);
      setPhase('done');
      setStatusMessage('Upload complete.');
    } catch (error: any) {
      console.error('File parsing/upload error:', error);
      setPhase('error');
      setProgress(0);
      setErrorMessage(error?.message || 'Upload failed. Please try a smaller file or clear old datasets.');
    }
  }, [onDataParsed, parseCSV, parseExcel]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (phase === 'parsing' || phase === 'saving') return;
    const file = acceptedFiles[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setPhase('error');
        setErrorMessage('File size must be less than 10MB.');
        return;
      }
      setStatusMessage(`Reading ${file.name}...`);
      parseFile(file);
    }
  }, [parseFile, phase]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024,
    disabled: phase === 'parsing' || phase === 'saving'
  });

  return (
    <div className="file-upload-container">
      <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''} ${phase === 'parsing' || phase === 'saving' ? 'busy' : ''}`}>
        <input {...getInputProps()} />
        <div className="upload-content">
          <div className="upload-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7,10 12,5 17,10" />
              <line x1="12" y1="5" x2="12" y2="15" />
            </svg>
          </div>
          <h3>Upload your data</h3>
          <p>
            {isDragActive 
              ? 'Drop your file here...' 
              : 'Drag & drop your CSV or Excel file here, or click to browse'
            }
          </p>
          <div className="file-info">
            <span>Supported formats: CSV, XLSX, XLS</span>
            <span>Maximum file size: 10MB</span>
          </div>
          {(phase === 'parsing' || phase === 'saving' || phase === 'done') && (
            <div className="upload-progress">
              <div className="progress-header">
                <span>{statusMessage}</span>
                <span>{progress}%</span>
              </div>
              <div className="progress-track">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <p className="progress-note">
                {phase === 'saving' ? 'Uploading and indexing dataset. This can take a few seconds for large files.' : ''}
              </p>
            </div>
          )}
          {errorMessage && <div className="upload-error">{errorMessage}</div>}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
